// src/pages/Subscription/index.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CommonDataTable from "../../common/DataTable";
import { getSubscriptionColumns } from "../../Components/Common/columns/Subscriptions";
import {
  fetchAllSubscriptions,
  fetchSubscriptionById,
} from "../../slices/subscription/thunk";
import { clearSubscriptionDetails } from "../../slices/subscription/reducer";

const INITIAL_FILTERS = {
  page: 1,
  currentPage: 1,
  limit: 10,
  perPage: 10,
  search: "",
  status: "",
  planType: "",
  orderBy: "createdAt",
  order: "desc",
  sort: "desc",
};

const SubscriptionPage = () => {
  const dispatch = useDispatch();

  const [filterParams, setFilterParams] = useState(INITIAL_FILTERS);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const {
    subscriptions,
    totalRows,
    loading,
    subscriptionDetails,
  } = useSelector((state) => state.Subscription || {});

  const fetchData = useCallback(
    (params = {}) => {
      setFilterParams((prev) => {
        const limit = params.perPage || params.limit || prev.limit || 10;
        const page = params.page || params.currentPage || prev.page || 1;

        const resolvedOrder = params.order || prev.order || "desc";
        const next = {
          page,
          currentPage: page,
          limit,
          perPage: limit,
          search: params.search !== undefined ? params.search : prev.search,
          status: params.status !== undefined ? params.status : prev.status,
          planType: params.planType !== undefined ? params.planType : prev.planType,
          orderBy: params.orderBy || prev.orderBy || "createdAt",
          order: resolvedOrder,
          sort: params.sort || prev.sort || resolvedOrder,
        };

        dispatch(fetchAllSubscriptions(next));
        return next;
      });
    },
    [dispatch]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (viewModalOpen && selectedSubscription) {
      // Try all possible ID fields that your API might accept
      const identifier =
        selectedSubscription.id || // Most common
        selectedSubscription.subscriptionId ||
        selectedSubscription._id ||
        selectedSubscription.subscriptionDbId;

      if (identifier) {
        dispatch(fetchSubscriptionById(identifier));
      } else {
        console.error("No valid identifier found:", selectedSubscription);
        dispatch(setError("No valid subscription identifier found"));
      }
    }
  }, [viewModalOpen, selectedSubscription, dispatch]);

  const handleRowClick = (row) => {
    console.log("Row data:", row);
    console.log("Available ID fields:", {
      id: row.id,
      subscriptionId: row.subscriptionId,
      _id: row._id,
      subscriptionDbId: row.subscriptionDbId
    });

    setSelectedSubscription(row);
    setViewModalOpen(true);
  };

  const handleModalClose = () => {
    setViewModalOpen(false);
    setSelectedSubscription(null);
    dispatch(clearSubscriptionDetails());
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      const date = new Date(value);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (error) {
      return value;
    }
  };

  const handleDownloadInvoice = useCallback((row) => {
    const invoiceUrl = row.invoice_pdf || row.hosted_invoice_url;
    if (invoiceUrl) {
      window.open(invoiceUrl, "_blank", "noopener");
    }
  }, []);

  const formatCurrency = (amount, currency) => {
    if (amount === undefined || amount === null || amount === "") {
      return "-";
    }
    const normalizedCurrency = (currency || "USD").toUpperCase();
    return `${amount} ${normalizedCurrency}`;
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return "badge-soft-secondary";
    const normalized = status.toLowerCase();
    if (normalized === "active" || normalized === "paid") return "badge-soft-success";
    if (normalized === "canceled" || normalized === "cancelled" || normalized === "unpaid")
      return "badge-soft-danger";
    if (normalized === "past_due") return "badge-soft-warning";
    return "badge-soft-secondary";
  };

  const columns = useMemo(
    () =>
      getSubscriptionColumns({
        onDownloadInvoice: handleDownloadInvoice,
      }),
    [handleDownloadInvoice]
  );

  const displaySubscription = subscriptionDetails || selectedSubscription;
  const subscriptionHistory = useMemo(() => {
    if (!displaySubscription || !Array.isArray(subscriptions)) return [];
    const key =
      displaySubscription.user_id ||
      displaySubscription.user_email ||
      displaySubscription.user_mobile;
    if (!key) return [];

    return subscriptions
      .filter((sub) => {
        const subKey = sub.user_id || sub.user_email || sub.user_mobile;
        const sameRecord =
          (sub._id || sub.id) ===
          (displaySubscription._id || displaySubscription.id);
        return subKey && subKey === key && !sameRecord;
      })
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const dateB = new Date(b.createdAt || b.updatedAt || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 5);
  }, [subscriptions, displaySubscription]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Subscriptions" pageTitle="Subscriptions" />
        <CommonDataTable
          columns={columns}
          data={subscriptions || []}
          totalRows={totalRows || 0}
          loading={loading}
          fetchData={fetchData}
          filterParams={filterParams}
          searchEnable
          checkboxEnabled={true}
          showExportButton
          showAddButton={false}
          exportFileName="subscriptions"
          onRowClick={handleRowClick}
        />

        <Modal
          isOpen={viewModalOpen}
          toggle={handleModalClose}
          size="lg"
          modalClassName="subscription-modal"
        >
          <ModalHeader toggle={handleModalClose} className="modal-title text-body fw-semibold">
            Subscription Details
          </ModalHeader>
          <ModalBody>
            {displaySubscription ? (
              <div className="subscription-details">
                <Card className="border-0 shadow-sm mb-4">
                  <CardBody className="d-flex flex-wrap gap-3 justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1">User</p>
                      <h4 className="mb-1 text-body">
                        {displaySubscription.user_name || "-"}
                      </h4>
                      <p className="mb-0">
                        {displaySubscription.user_email ? (
                          <a href={`mailto:${displaySubscription.user_email}`}>
                            {displaySubscription.user_email}
                          </a>
                        ) : (
                          "-"
                        )}
                      </p>
                      <p className="mb-0">
                        {displaySubscription.user_mobile ? (
                          <a href={`tel:${displaySubscription.user_mobile}`}>
                            {displaySubscription.user_mobile}
                          </a>
                        ) : (
                          "-"
                        )}
                      </p>
                    </div>
                    <div className="text-end">
                      <p className="text-muted mb-1">Status</p>
                      <span
                        className={`badge ${getStatusBadgeClass(
                          displaySubscription.status || displaySubscription.invoice_status
                        )}`}
                      >
                        {(displaySubscription.status ||
                          displaySubscription.invoice_status ||
                          "-"
                        ).toUpperCase()}
                      </span>
                      <p className="text-muted mb-1 mt-3">Plan</p>
                      <h6 className="mb-0 text-body">
                        {displaySubscription.plan_label ||
                          displaySubscription.plan_type ||
                          "-"}
                      </h6>
                      <small className="text-muted">
                        {displaySubscription.interval
                          ? `${displaySubscription.interval_count || 1
                          } ${displaySubscription.interval}`
                          : "Plan duration unavailable"}
                      </small>
                    </div>
                  </CardBody>
                </Card>

                <Row className="g-3">
                  <Col md={6}>
                    <Card className="border shadow-sm h-100">
                      <CardBody>
                        <h6 className="text-muted text-uppercase mb-3">
                          Subscription
                        </h6>
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">ID</span>
                            <span className="fw-semibold text-body text-end">
                              {displaySubscription.subscriptionId ||
                                displaySubscription.id ||
                                "-"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Type</span>
                            <span className="fw-semibold text-body text-end">
                              {displaySubscription.interval
                                ? `${displaySubscription.interval_count || 1
                                } ${displaySubscription.interval}`
                                : "-"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Auto Pay</span>
                            <span className="fw-semibold text-body text-end">
                              {displaySubscription.has_autopay === true
                                ? "Enabled"
                                : displaySubscription.has_autopay === false
                                  ? "Disabled"
                                  : "-"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Cancel at Period End</span>
                            <span className="fw-semibold text-body text-end">
                              {displaySubscription.cancelAtPeriodEnd === true
                                ? "Yes"
                                : displaySubscription.cancelAtPeriodEnd === false
                                  ? "No"
                                  : "-"}
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border shadow-sm h-100">
                      <CardBody>
                        <h6 className="text-muted text-uppercase mb-3">
                          Billing
                        </h6>
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Amount</span>
                            <span className="fw-semibold text-body text-end">
                              {formatCurrency(
                                displaySubscription.invoice_amount_paid ??
                                displaySubscription.amount,
                                displaySubscription.currency ||
                                displaySubscription.invoice_currency
                              )}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Payment Type</span>
                            <span className="fw-semibold text-body text-end text-uppercase">
                              {displaySubscription.payment_method_type ||
                                displaySubscription.payment_type ||
                                "-"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Invoice Status</span>
                            <span className="fw-semibold text-body text-end">
                              {(displaySubscription.invoice_status || "-").toUpperCase()}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Invoice ID</span>
                            <span className="fw-semibold text-body text-end">
                              {displaySubscription.invoice_id || "-"}
                            </span>
                          </div>
                        </div>
                        {(displaySubscription.invoice_pdf ||
                          displaySubscription.hosted_invoice_url) && (
                            <Button
                              size="sm"
                              color="primary"
                              className="mt-3"
                              onClick={() => handleDownloadInvoice(displaySubscription)}
                            >
                              <i className="ri-download-2-line me-1" />
                              Download Invoice
                            </Button>
                          )}
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={12}>
                    <Card className="border shadow-sm h-100">
                      <CardBody>
                        <h6 className="text-muted text-uppercase mb-3">
                          Timeline
                        </h6>
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Start</span>
                            <span className="fw-semibold text-body text-end">
                              {formatDateTime(
                                displaySubscription.start_date ||
                                displaySubscription.current_period_start
                              )}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Current Period End</span>
                            <span className="fw-semibold text-body text-end">
                              {formatDateTime(
                                displaySubscription.current_period_end ||
                                displaySubscription.period_end
                              )}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Created</span>
                            <span className="fw-semibold text-body text-end">
                              {formatDateTime(displaySubscription.createdAt || displaySubscription.created_at)}
                            </span>
                          </div>
                          {displaySubscription.canceled_at && (
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Canceled At</span>
                              <span className="fw-semibold text-body text-end">
                                {formatDateTime(displaySubscription.canceled_at)}
                              </span>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </div>
            ) : (
              <div className="text-center py-4">
                <p>Loading subscription details...</p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleModalClose}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default SubscriptionPage;